// Education narrative data utilities for aggregating education facility data
// Reuses existing education chart utilities to maintain consistency

import {
  calculateOwnershipDistribution,
  calculateEducationLevelDistribution,
  calculateGenderBalance,
  calculateTeacherQualifications,
  calculateInfrastructureReadiness,
  calculateResourceRatios,
  calculateWASHCompliance,
} from './educationChartUtils'
import { findLocationById } from './narrativeDataUtils'

/**
 * Filter schools by location hierarchy using location codes
 */
function filterSchoolsByLocation(schools, location) {
  if (!schools || !location) return []

  const locationCode = location.code

  // Filter based on location code prefix matching
  return schools.filter(school => {
    if (!school.location_code) return false

    // Match by checking if facility's location_code starts with location's code
    // This works because:
    // - District D01 matches D01xxx
    // - Subcounty D01S01 matches D01S01xxx
    // - Parish D01S01P01 matches D01S01P01xxx
    // - Village D01S01P01V01 matches exact code
    return school.location_code.startsWith(locationCode)
  })
}

/**
 * Calculate average infrastructure percentage
 */
function calculateAvgInfrastructure(infrastructureData) {
  if (!infrastructureData || typeof infrastructureData !== 'object') return 0

  const values = Object.values(infrastructureData).filter(
    v => typeof v === 'number'
  )
  if (values.length === 0) return 0

  const sum = values.reduce((acc, val) => acc + val, 0)
  return Math.round(sum / values.length)
}

/**
 * Identify critical infrastructure gaps (below 50% availability)
 */
function identifyInfrastructureGaps(infrastructureData) {
  const gaps = []
  const categories = [
    { key: 'electricity', name: 'Electricity', severity: 'high' },
    { key: 'water', name: 'Water Supply', severity: 'high' },
    { key: 'ictLab', name: 'ICT Laboratory', severity: 'medium' },
    { key: 'handwashing', name: 'Handwashing Facilities', severity: 'high' },
    {
      key: 'genderToilets',
      name: 'Gender-Separated Toilets',
      severity: 'high',
    },
    {
      key: 'accessibleToilets',
      name: 'Accessible Toilets',
      severity: 'medium',
    },
  ]

  categories.forEach(cat => {
    const percentage = infrastructureData[cat.key] || 0
    if (percentage < 50) {
      gaps.push({
        category: cat.key,
        name: cat.name,
        percentage,
        severity: cat.severity,
      })
    }
  })

  return gaps.sort((a, b) => a.percentage - b.percentage) // Lowest coverage first
}

/**
 * Calculate staffing distribution for education
 */
function calculateStaffingDistribution(schools) {
  const totals = {
    totalTeachers: 0,
    maleTeachers: 0,
    femaleTeachers: 0,
    degreeTeachers: 0,
    diplomaTeachers: 0,
    certificateTeachers: 0,
    govtPayroll: 0,
  }

  schools.forEach(school => {
    totals.totalTeachers += school.total_teachers || 0
    totals.maleTeachers += school.male_teachers || 0
    totals.femaleTeachers += school.female_teachers || 0
    totals.degreeTeachers += school.degree_teachers || 0
    totals.diplomaTeachers += school.diploma_teachers || 0
    totals.certificateTeachers += school.certificate_teachers || 0
    totals.govtPayroll += school.govt_payroll_teachers || 0
  })

  // Calculate percentages
  const distribution = []
  if (totals.degreeTeachers > 0) {
    distribution.push({
      name: 'Degree Holders',
      value: totals.degreeTeachers,
      percentage: Math.round(
        (totals.degreeTeachers / totals.totalTeachers) * 100
      ),
    })
  }
  if (totals.diplomaTeachers > 0) {
    distribution.push({
      name: 'Diploma Holders',
      value: totals.diplomaTeachers,
      percentage: Math.round(
        (totals.diplomaTeachers / totals.totalTeachers) * 100
      ),
    })
  }
  if (totals.certificateTeachers > 0) {
    distribution.push({
      name: 'Certificate Holders',
      value: totals.certificateTeachers,
      percentage: Math.round(
        (totals.certificateTeachers / totals.totalTeachers) * 100
      ),
    })
  }

  return {
    totals,
    averages: {
      teachersPerSchool:
        schools.length > 0
          ? Math.round(totals.totalTeachers / schools.length)
          : 0,
    },
    distribution,
  }
}

/**
 * Get sub-location comparisons for education
 */
function getSubLocationComparisons(allSchools, location, locations, maxDepth) {
  // Determine what sub-locations to compare
  let comparisonLocations = []
  let comparisonType = ''

  if (location.type === 'district' && maxDepth !== 'district') {
    comparisonLocations = location.children || []
    comparisonType = 'subcounties'
  } else if (location.type === 'subcounty' && maxDepth === 'village') {
    comparisonLocations = location.children || []
    comparisonType = 'parishes'
  } else {
    // No sub-location comparisons available
    return null
  }

  if (comparisonLocations.length === 0) {
    return null
  }

  // Calculate metrics for each sub-location
  const locationStats = comparisonLocations.map(subLoc => {
    const subLocSchools = filterSchoolsByLocation(
      allSchools,
      { ...subLoc, parent: location },
      locations
    )

    const infrastructureReadiness =
      calculateInfrastructureReadiness(subLocSchools)
    const resourceRatios = calculateResourceRatios(subLocSchools)
    const avgInfrastructure = calculateAvgInfrastructure(
      infrastructureReadiness
    )

    // Safely get pupil-teacher ratio with null check
    const ptr = resourceRatios?.pupilTeacherRatio || 0

    // Calculate a composite quality score (0-100)
    const compositeScore = Math.round(
      avgInfrastructure * 0.4 + // Infrastructure weight: 40%
        (ptr > 0 && ptr <= 40 ? 30 : ptr > 0 ? 15 : 0) + // PTR weight: 30%
        (subLocSchools.length > 0 ? 30 : 0) // Coverage weight: 30%
    )

    return {
      id: subLoc.id,
      name: subLoc.name,
      totalSchools: subLocSchools.length,
      facilityCount: subLocSchools.length, // For LocationRanking compatibility
      avgPupilTeacherRatio: ptr,
      avgServiceCoverage: ptr > 0 && ptr <= 40 ? 80 : ptr > 0 ? 60 : 0, // For LocationRanking compatibility
      avgInfrastructure,
      compositeScore,
      rank: 0, // Will be set after sorting
    }
  })

  // Sort by composite score and add ranks
  const rankings = locationStats
    .sort((a, b) => b.compositeScore - a.compositeScore)
    .map((loc, index) => ({
      ...loc,
      rank: index + 1,
    }))

  return {
    type: comparisonType,
    level: location.type,
    context: location.name,
    rankings,
  }
}

/**
 * Aggregate education data for a specific location
 * Main export function that mimics the health aggregation structure
 */
export function aggregateEducationLocationData(
  schools,
  locationId,
  locations,
  maxDepth = 'village'
) {
  // Find location
  const location = findLocationById(locations, locationId)
  if (!location) {
    throw new Error(`Location ${locationId} not found`)
  }

  // Filter schools for this location
  const locationSchools = filterSchoolsByLocation(schools, location, locations)

  if (locationSchools.length === 0) {
    // Return empty data structure if no schools
    return {
      location,
      metrics: {
        totalInstitutions: 0,
        institutionDistribution: {},
        ownershipDistribution: {},
        infrastructureAvailability: {},
        teacherQualifications: {
          total: 0,
          degree: 0,
          diploma: 0,
          certificate: 0,
        },
        educationRatios: {},
        genderParity: { boysCount: 0, girlsCount: 0, parityIndex: 1.0 },
        washCompliance: {},
        infrastructureGaps: [],
        staffingDistribution: {
          totals: {},
          averages: {},
          distribution: [],
        },
        totalLearners: 0,
        totalTeachers: 0,
      },
      subLocationComparisons: null,
    }
  }

  // Calculate metrics using existing education utilities
  const institutionDistribution =
    calculateEducationLevelDistribution(locationSchools)
  const ownershipDistribution = calculateOwnershipDistribution(locationSchools)

  // Infrastructure readiness returns array, transform to object for charts
  const infrastructureArray = calculateInfrastructureReadiness(locationSchools)
  const infrastructureAvailability = infrastructureArray.reduce((acc, item) => {
    // Map to chart-expected keys
    const keyMap = {
      Electricity: 'electricity',
      'Water Supply': 'water',
      'ICT Laboratory': 'ictLab',
      'Handwashing Facilities': 'handwashing',
      'Gender-Separated Toilets': 'genderToilets',
      'Accessible Toilets': 'accessibleToilets',
      'Teacher Toilets': 'teacherToilets',
    }
    const key = keyMap[item.name] || item.name
    acc[key] = item.percentage
    return acc
  }, {})

  // Teacher qualifications returns array, extract totals and create object
  const teacherQualArray = calculateTeacherQualifications(locationSchools)
  const teacherQualifications =
    Array.isArray(teacherQualArray) && teacherQualArray.length > 0
      ? {
          total: teacherQualArray.reduce(
            (sum, item) => sum + (item.value || 0),
            0
          ),
          degree:
            teacherQualArray.find(item => item.name === 'Degree')?.value || 0,
          diploma:
            teacherQualArray.find(item => item.name === 'Diploma')?.value || 0,
          certificate:
            teacherQualArray.find(item => item.name === 'Certificate')?.value ||
            0,
        }
      : { total: 0, degree: 0, diploma: 0, certificate: 0 }

  const educationRatios = calculateResourceRatios(locationSchools)

  // Gender balance returns object, transform to expected format and convert ratio to number
  const genderBalanceData = calculateGenderBalance(locationSchools)
  const genderParity = {
    boysCount: genderBalanceData.boys || 0,
    girlsCount: genderBalanceData.girls || 0,
    parityIndex:
      typeof genderBalanceData.ratio === 'string'
        ? parseFloat(genderBalanceData.ratio) || 1.0
        : genderBalanceData.ratio || 1.0,
  }

  const washCompliance = calculateWASHCompliance(locationSchools)
  const infrastructureGaps = identifyInfrastructureGaps(
    infrastructureAvailability
  )
  const staffingDistribution = calculateStaffingDistribution(locationSchools)

  // Calculate totals
  const totalLearners = locationSchools.reduce(
    (sum, school) => sum + (school.total_learners || 0),
    0
  )
  const totalTeachers = locationSchools.reduce(
    (sum, school) => sum + (school.total_teachers || 0),
    0
  )

  // Get sub-location comparisons
  const subLocationComparisons = getSubLocationComparisons(
    schools, // Use ALL schools for comparisons
    location,
    locations,
    maxDepth
  )

  return {
    location,
    metrics: {
      totalInstitutions: locationSchools.length,
      institutionDistribution,
      ownershipDistribution,
      infrastructureAvailability,
      teacherQualifications,
      educationRatios,
      genderParity,
      washCompliance,
      infrastructureGaps,
      staffingDistribution,
      totalLearners,
      totalTeachers,
    },
    subLocationComparisons,
  }
}

/**
 * Calculate key statistics for education narrative
 */
export function calculateEducationKeyStatistics(metrics) {
  return {
    totalInstitutions: metrics.totalInstitutions || 0,
    avgPupilTeacherRatio: Math.round(
      metrics.educationRatios?.pupilTeacherRatio || 0
    ),
    avgInfrastructure: calculateAvgInfrastructure(
      metrics.infrastructureAvailability || {}
    ),
    criticalGaps: (metrics.infrastructureGaps || []).length,
    genderParityIndex: metrics.genderParity?.parityIndex || 1.0,
    totalLearners: metrics.totalLearners || 0,
    totalTeachers: metrics.totalTeachers || 0,
  }
}
